import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import "../../../assets/styles/custom-styles.css";
import { kitFields } from "../../../constants/forms/kitFields";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditKitPage = () => {
  const navigate = useNavigate();
  const { equipmentKitId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits",
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  // MUDANÇA AQUI: Passa `kitFields` para o `useForm`
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(kitFields),
    kitFields,
  );

  // --- Query para buscar os detalhes do Kit ---
  const { data: kitData, isLoading: isLoadingKit } = useQueryWithLoader({
    queryKey: ["equipment_kit", equipmentKitId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.equipmentsKits.getById(equipmentKitId),
      );
      return response.result;
    },
    enabled: !!equipmentKitId,
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para preencher o formulário quando os dados do kit estiverem prontos ---
  useEffect(() => {
    if (kitData) {
      setFormData({
        kit: kitData,
      });
      hideLoader();
    }
  }, [kitData]);

  // --- Mutação para atualizar o Kit ---
  const updateKitMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.equipmentsKits.update(equipmentKitId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        navigate(`/aluguel/kits-equipamentos`, {
          state: { type: "success" },
        });
        queryClient.invalidateQueries({
          queryKey: ["equipment_kit", equipmentKitId],
        });
        queryClient.invalidateQueries({
          queryKey: ["equipments_kits", filters, pagination],
        });
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        kit: {
          ...cleanedData.kit,
        },
      };
      await updateKitMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar kit:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateKitMutation, equipmentKitId]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/kits-equipamentos");
  }, [navigate]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário (PADRÃO) ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return kitFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Kit"
        showBackButton={true}
        backUrl="/aluguel/kits-equipamentos"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Atualizar"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={isLoadingKit || updateKitMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditKitPage;
